/* eslint-disable formatjs/no-literal-string-in-jsx */
import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Dialog } from "common/core-ui/Dialog.tsx";
import { Section } from "routes/home/common/debug/Section.tsx";
import useVirtualStore from "routes/home/common/virtual-store/useVirtualStore.tsx";
import useDiagnosticChecklists from "./useDiagnosticChecklists.ts";
import {
  DiagnosticChecklist,
  DiagnosticChecklistItem,
} from "./diagnosticChecklistTypes.ts";
import styles from "./DiagnosticDialog.module.scss";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader.ts";

const statusWords = {
  pass: "Pass",
  fail: "Fail",
  info: "Info",
};

const statusClassNames = {
  pass: styles.statusPass,
  fail: styles.statusFail,
  info: styles.statusInfo,
};

function ChecklistItemRow({ item }: { item: DiagnosticChecklistItem }) {
  return (
    <li className={clsx(styles.checklistItem, statusClassNames[item.status])}>
      <strong>{statusWords[item.status]}:</strong> {item.label}
      {item.detail && item.detail.length > 0 && (
        <code className={styles.checklistDetail}>
          {item.detail.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </code>
      )}
    </li>
  );
}

/**
 * The shell below keeps this content in a separate component. The store
 * subscription then exists only while the dialog is open. A registrar makes
 * a new checklist on each render, and each new checklist tells the
 * subscribers. Thus a subscription while the dialog is closed renders this
 * component on each ProgramBanner countdown tick.
 */
function DiagnosticDialogContent({
  onRequestClose,
}: {
  onRequestClose: () => void;
}) {
  const registered = useDiagnosticChecklists();
  const { isVirtualStore } = useVirtualStore();
  const {
    config: {
      allowPostDeadlineShopping,
      allowPreDeadlineShopping,
      showEposItems,
    },
  } = useLayoutData();

  // The dialog builds this section. The store does not hold it, because the
  // only value comes from the brand loader. The dialog reads that value.
  const checklists: DiagnosticChecklist[] = [
    {
      id: "general",
      title: "General",
      items: [
        {
          label: "Store type",
          status: "info",
          detail: [isVirtualStore ? "Virtual Store" : "Compare"],
        },
        {
          label: "Allows shopping?",
          status: "info",
          detail: [
            `allowPostDeadlineShopping: ${allowPostDeadlineShopping}`,
            `allowPreDeadlineShopping: ${allowPreDeadlineShopping}`,
            `showEposItems: ${showEposItems}`,
          ],
        },
      ],
    },
    ...registered,
  ];

  return (
    <Dialog
      title="Diagnostics"
      darkHeader
      onRequestClose={onRequestClose}
      dialogClassName={styles.dialog}
    >
      {registered.length === 0 && (
        <p className={styles.hint}>
          Only general diagnostics are available now. Open the relevant feature,
          for example the Program Switcher. Then open this dialog again.
        </p>
      )}
      <div className={styles.checklistsRow}>
        {checklists.map((checklist) => (
          <Section key={checklist.id}>
            <h2>{checklist.title}</h2>
            <ul className={styles.checklist}>
              {checklist.items.map((item, i) => (
                <ChecklistItemRow key={i} item={item} />
              ))}
            </ul>
          </Section>
        ))}
      </div>
    </Dialog>
  );
}

export default function DiagnosticDialog() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.code === "KeyG" && e.altKey) {
        setIsOpen(true);
      }
    };

    window.addEventListener("keyup", listener);
    return () => {
      window.removeEventListener("keyup", listener);
    };
  }, []);

  if (isOpen) {
    return <DiagnosticDialogContent onRequestClose={() => setIsOpen(false)} />;
  }
}
